/**
 * useAccountWoW
 *
 * Fans out month-to-date traffic summaries across every active property in
 * parallel via the external traffic API (`getTrafficSummary`). One property
 * pays two requests (this month-to-date + the same span last month) per
 * refresh; React Query caches each for 5 minutes.
 *
 * The dashboard reports on a month-to-date cadence (SUPENG-96): the sites
 * table, header KPI strip, and insight banner all compare THIS month so far
 * against the SAME number of days in the prior month (an honest MoM-to-date
 * delta rather than partial-month vs full-month).
 *
 * NOTE: the public field/type names still read `thisWeek` / `lastWeek` /
 * `change` for backwards-compatibility with existing consumers — the values
 * they carry are now month-to-date and month-over-month respectively.
 *
 * Returns a per-property map of this-period vs prior-period stats plus the
 * change deltas. Drives:
 *   - SitesGrid "Views MTD" + MoM columns
 *   - CombinedInsightAgent variant cascade (top-mover is now MoM)
 *   - Header KPI strip (via totalViewsThisWeek == views MTD)
 *
 * Cost: 2N parallel HTTP requests on dashboard mount. For 7-site accounts
 * this is acceptable — same order of magnitude as existing per-property fetches.
 */

import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { format, startOfMonth, subMonths } from 'date-fns'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getTrafficSummary } from '@/lib/api/external-api'

export interface WeeklySummary {
  thisWeek: {
    views: number
    sessions: number
    bounceRate: number
    avgSessionDuration: number
    top_pages?: Array<{ page: string; page_views: number }>
    top_referrers?: Array<{ referrer: string; count: number }>
    conversion_rate?: number
  }
  lastWeek: {
    views: number
    sessions: number
    bounceRate: number
    avgSessionDuration: number
  }
  change: {
    views: number
    sessions: number
    bounceRate: number
    avgSessionDuration: number
  }
}

interface UseAccountWoWInput {
  properties: Array<{ propertyId: string; isDemo?: boolean; archived?: boolean }>
}

interface UseAccountWoWResult {
  /** Per-property weekly summaries, keyed by propertyId. */
  byPropertyId: Record<string, WeeklySummary>
  /** WoW view delta % per property (rounded to int). Feeds TOP_MOVER directly. */
  deltasByPropertyId: Record<string, number>
  /** Aggregate views across all properties for the current 7 days. */
  totalViewsThisWeek: number
  /** Aggregate views across all properties for the prior 7 days. */
  totalViewsLastWeek: number
  /** Aggregate WoW delta as a rounded percent. */
  totalDeltaPct: number | null
  isLoading: boolean
}

/**
 * Prior comparison window for MoM-to-date: the first day of last month through
 * the same day-of-month as today, as `YYYY-MM-DD_YYYY-MM-DD` for the external
 * API. `subMonths` clamps overflow (e.g. Mar 31 → Feb 28) so the range is
 * always valid. This makes "this month so far" comparable to "the same span
 * last month" rather than to a full prior month.
 */
function priorMonthToDatePair(): string {
  const priorEnd = subMonths(new Date(), 1)
  const priorStart = startOfMonth(priorEnd)
  return `${format(priorStart, 'yyyy-MM-dd')}_${format(priorEnd, 'yyyy-MM-dd')}`
}

function pctChange(current: number, previous: number): number {
  if (previous <= 0) return 0
  return ((current - previous) / previous) * 100
}

function mapSummaryResponse(
  thisWeekRes: Awaited<ReturnType<typeof getTrafficSummary>> | null,
  lastWeekRes: Awaited<ReturnType<typeof getTrafficSummary>> | null,
): WeeklySummary {
  const tw = thisWeekRes?.data
  const lw = lastWeekRes?.data
  const thisWeekViews = tw?.page_views ?? 0
  const lastWeekViews = lw?.page_views ?? 0
  const thisWeekSessions = tw?.total_sessions ?? 0
  const lastWeekSessions = lw?.total_sessions ?? 0

  return {
    thisWeek: {
      views: thisWeekViews,
      sessions: thisWeekSessions,
      bounceRate: tw?.bounce_rate ?? 0,
      avgSessionDuration: tw?.avg_session_duration ?? 0,
    },
    lastWeek: {
      views: lastWeekViews,
      sessions: lastWeekSessions,
      bounceRate: lw?.bounce_rate ?? 0,
      avgSessionDuration: lw?.avg_session_duration ?? 0,
    },
    change: {
      views: pctChange(thisWeekViews, lastWeekViews),
      sessions: pctChange(thisWeekSessions, lastWeekSessions),
      bounceRate: pctChange(tw?.bounce_rate ?? 0, lw?.bounce_rate ?? 0),
      avgSessionDuration: pctChange(
        tw?.avg_session_duration ?? 0,
        lw?.avg_session_duration ?? 0,
      ),
    },
  }
}

export function useAccountWoW({ properties }: UseAccountWoWInput): UseAccountWoWResult {
  const { token } = useAuth()

  const targetIds = useMemo(
    () =>
      properties
        .filter((p) => !p.isDemo && !p.archived)
        .map((p) => p.propertyId),
    [properties],
  )

  const priorPeriod = useMemo(() => priorMonthToDatePair(), [])

  const results = useQueries({
    queries: targetIds.map((propertyId) => ({
      queryKey: ['account-traffic-mtd', propertyId, priorPeriod],
      queryFn: async (): Promise<WeeklySummary | null> => {
        if (!token) return null
        try {
          const [thisWeekRes, lastWeekRes] = await Promise.all([
            getTrafficSummary('MONTH_TO_DATE', propertyId, token),
            getTrafficSummary(priorPeriod, propertyId, token),
          ])
          return mapSummaryResponse(thisWeekRes, lastWeekRes)
        } catch {
          return null
        }
      },
      enabled: !!token && !!propertyId,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  })

  return useMemo<UseAccountWoWResult>(() => {
    const byPropertyId: Record<string, WeeklySummary> = {}
    const deltasByPropertyId: Record<string, number> = {}
    let totalViewsThisWeek = 0
    let totalViewsLastWeek = 0
    let anyLoading = false

    targetIds.forEach((propertyId, i) => {
      const q = results[i]
      if (q?.isLoading) anyLoading = true
      const data = q?.data
      if (!data) return
      byPropertyId[propertyId] = data
      totalViewsThisWeek += data.thisWeek?.views ?? 0
      totalViewsLastWeek += data.lastWeek?.views ?? 0
      deltasByPropertyId[propertyId] = Math.round(data.change?.views ?? 0)
    })

    const totalDeltaPct =
      totalViewsLastWeek > 0
        ? Math.round(
            ((totalViewsThisWeek - totalViewsLastWeek) / totalViewsLastWeek) * 100,
          )
        : null

    return {
      byPropertyId,
      deltasByPropertyId,
      totalViewsThisWeek,
      totalViewsLastWeek,
      totalDeltaPct,
      isLoading: anyLoading,
    }
  }, [results, targetIds])
}
